/**
 * The top bar's "Robots online" count, by presence (twin slice 1, plan W3
 * step 14).
 *
 * It used to count every record whose status was not `offline` or
 * `pending-localize`. A robot that went silent keeps its last status
 * forever, so a fleet whose feed had died still read "2 online".
 *
 * "0" MEANS CHECKED AND NONE; null ("?") MEANS COULD NOT CHECK:
 *
 *   - the feed is clamped: nothing on screen is current, so neither is a
 *     count of what is;
 *   - any counted robot is still `checking` (one receipt, or none, since
 *     the stream opened): it could be reporting or a replay, and a number
 *     that leaves it out would read as "checked, and it is not". That
 *     includes a robot riding a lift, or settling after one, that has
 *     been seen only once (presence's `checking` flag): riding comes
 *     before the two-receipt rule, so a replayed row of a robot silent
 *     for minutes inside a car reads riding, and counting it would read
 *     "1 online";
 *   - any counted robot has no presence at all.
 *
 * ONLINE is reporting within the last 30 s: `live`, `quiet` (10-30 s,
 * drawn dimmed, not yet "No signal"), `settling` after a ride, and
 * `riding`. Not `lost` and not `unknown` (on, but lost its place: it
 * cannot take work, which is what the old rule excluded it for too).
 *
 * A site audience never counts the simulated fleet.
 */
import type { RobotStateView } from '../api/rmf-client';
import { robotReceiptKey } from '../api/robot-receipts';
import type { PresenceKind, RobotPresence } from '../screens/Map/robot-presence';
import { isSimulatedFleet } from '../state/simulated-fleet';

const ONLINE: ReadonlySet<PresenceKind> = new Set<PresenceKind>(['live', 'quiet', 'settling', 'riding']);

export function robotsOnline(
  robots: readonly Pick<RobotStateView, 'name' | 'fleet'>[],
  presence: ReadonlyMap<string, RobotPresence>,
  opts: { clamped: boolean; showSimulated: boolean },
): number | null {
  if (opts.clamped) return null;
  let n = 0;
  for (const robot of robots) {
    if (!opts.showSimulated && isSimulatedFleet(robot.fleet)) continue;
    const p = presence.get(robotReceiptKey(robot.fleet, robot.name));
    if (!p || p.checking) return null;
    if (ONLINE.has(p.kind)) n += 1;
  }
  return n;
}
